import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Code2, Network, BarChart3, GitBranch, Zap, Shield, Sparkles } from 'lucide-react';

function LandingPage() {
    const navigate = useNavigate();

    return (
        <div className="min-h-screen bg-background text-white relative overflow-hidden">
            {/* Hero Glow Effect */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-hero-glow pointer-events-none" />

            {/* Navbar */}
            <nav className="fixed top-0 w-full z-50 bg-black/50 backdrop-blur-lg border-b border-white/5">
                <div className="max-w-7xl mx-auto px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                            <Network className="w-6 h-6 text-black" />
                        </div>
                        <span className="font-heading text-xl font-bold">FlowCode</span>
                    </div>
                    <button
                        onClick={() => navigate('/dashboard')}
                        className="px-6 py-2 bg-white/5 border border-white/10 hover:bg-white/10 rounded-full transition-colors"
                        data-testid="navbar-login-btn"
                    >
                        Enter App
                    </button>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="relative pt-32 pb-20 px-8">
                <div className="max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center mb-16"
                    >
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full mb-8 backdrop-blur-md">
                            <Sparkles className="w-4 h-4 text-primary" />
                            <span className="text-sm text-text-secondary">Designed by Piyush Kumar, VIT Chennai</span>
                        </div>

                        <h1 className="font-heading font-bold text-6xl md:text-7xl tracking-tighter leading-none mb-6">
                            Visualize Workflows.
                            <br />
                            <span className="text-primary">Code in Real-Time.</span>
                        </h1>

                        <p className="text-xl text-text-secondary max-w-2xl mx-auto mb-12 leading-relaxed">
                            Create stunning visual workflows with tables, diagrams, graphs & trees.
                            Plus an integrated C compiler IDE for seamless development.
                        </p>

                        <div className="flex gap-4 justify-center">
                            <button
                                onClick={() => navigate('/dashboard')}
                                className="neon-button"
                                data-testid="hero-get-started-btn"
                            >
                                Launch Dashboard
                            </button>
                            <button
                                className="px-8 py-3 bg-white/5 border border-white/10 hover:bg-white/10 rounded-full backdrop-blur-md transition-colors"
                            >
                                Learn More
                            </button>
                        </div>
                    </motion.div>

                    {/* Hero Image */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1, delay: 0.2 }}
                        className="relative"
                    >
                        <div className="glass-card p-8 max-w-5xl mx-auto">
                            <img
                                src="https://images.unsplash.com/photo-1653227709410-8be8c731c08a?crop=entropy&cs=srgb&fm=jpg&q=85"
                                alt="3D Workflow Visualization"
                                className="w-full h-auto rounded-xl"
                            />
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-20 px-8 relative">
                <div className="max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-4">
                            Everything You Need
                        </h2>
                        <p className="text-lg text-text-secondary">Powerful features for modern developers</p>
                    </motion.div>

                    <div className="grid md:grid-cols-3 gap-6">
                        {[
                            {
                                icon: Network,
                                title: '4 Workflow Types',
                                description: 'Create tables, diagrams, graphs, and tree structures with drag-and-drop ease',
                                color: 'text-primary'
                            },
                            {
                                icon: Code2,
                                title: 'Integrated C IDE',
                                description: 'Write, compile, and run C code with syntax highlighting and real-time execution',
                                color: 'text-accent-cyan'
                            },
                            {
                                icon: Shield,
                                title: 'Secure Execution',
                                description: 'Docker-sandboxed environment ensures safe code execution with resource limits',
                                color: 'text-accent-pink'
                            },
                            {
                                icon: GitBranch,
                                title: 'Node Connections',
                                description: 'Connect workflow nodes with custom relationships and interactive visualizations',
                                color: 'text-primary'
                            },
                            {
                                icon: BarChart3,
                                title: 'Export to PDF/PPT',
                                description: 'Generate professional presentations and documents from your workflows instantly',
                                color: 'text-accent-yellow'
                            },
                            {
                                icon: Zap,
                                title: 'Prompt Library',
                                description: 'Pre-built templates and custom prompts to accelerate your workflow creation',
                                color: 'text-accent-cyan'
                            }
                        ].map((feature, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                viewport={{ once: true }}
                                className="glass-card p-6 hover:bg-white/10 transition-all duration-300 group"
                            >
                                <feature.icon className={`w-12 h-12 ${feature.color} mb-4 transition-transform group-hover:scale-110`} />
                                <h3 className="font-heading text-2xl font-medium mb-2">{feature.title}</h3>
                                <p className="text-text-secondary leading-relaxed">{feature.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}\n      <section className="py-20 px-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className="max-w-4xl mx-auto text-center"
                >
                    <div className="glass-card p-12">
                        <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-6">
                            Ready to Build?
                        </h2>
                        <p className="text-lg text-text-secondary mb-8">
                            Join developers creating beautiful workflows and coding in real-time
                        </p>
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="neon-button"
                            data-testid="cta-get-started-btn"
                        >
                            Start Creating Now
                        </button>
                    </div>
                </motion.div>
            </section>

            {/* Footer */}
            <footer className="border-t border-white/5 py-8 px-8">
                <div className="max-w-7xl mx-auto text-center text-text-muted text-sm">
                    <p>\u00a9 2026 FlowCode. Designed & Built by Piyush Kumar, VIT Chennai</p>
                </div>
            </footer>
        </div>
    );
}

export default LandingPage;
